import { IconExternalLink, IconFileExport, IconPlus, IconSearch } from "@tabler/icons-react";
import pluralize from "pluralize";
import { createContext, forwardRef, PropsWithChildren, useContext } from "react";

import { Anchor, Badge, Button, type ButtonProps, Flex, FlexProps, InfoTooltip, Loader, Text, TextInput, Title } from "~/ui";

import { FiltersButton } from "./filters/FiltersButton";
import styles from "./ModuleHeader.module.css";

type ModuleHeaderProps = {
  name: string;
  description?: string;
  descriptionLinkLabel?: string;
  descriptionLinkHref?: string;
};
type ModuleTitleProps = {
  plural?: boolean;
  helpText?: string;
  /**
   * Rendered right after the title, centred on it, e.g. `<BetaBadge />`. An element, not any node, so a conditional
   * caller writes `badge={isBeta ? <BetaBadge /> : undefined}` and never renders an empty badge slot.
   */
  badge?: React.ReactElement;
};
type ModuleSearchProps = {
  placeholder?: string;
  query: string;
  setQuery: (query: string) => void;
};
type ModuleExportProps = {
  exportOnClick?: () => void;
  exportIsLoading?: boolean;
  rightIcon?: React.ReactNode;
} & Pick<ButtonProps, "variant" | "color" | "size" | "disabled">;
type ModuleFiltersProps = {
  nFiltersApplied?: number;
  openFilterDrawer?: () => void;
};
type ModuleNewProps = {
  label?: string;
  openCreateModal?: () => void;
  disabled?: boolean;
  /** Count shown as a badge on the button (e.g. pending orders); hidden when undefined or 0. */
  badgeCount?: number;
};

const ModuleHeaderContext = createContext<ModuleHeaderProps>({ name: "" });

const ModuleTitle = ({ plural = true, helpText, badge, ...props }: PropsWithChildren<ModuleTitleProps>) => {
  const { name } = useContext(ModuleHeaderContext);
  const title = (
    <Flex align="center" gap="xs">
      <Title order={4} fw={700}>
        {plural ? pluralize(name) : name}
      </Title>
      {/* Centred on the title's line box the badge reads slightly high; the nudge seats it on the title's visual centre. */}
      {badge !== undefined && <Flex style={{ transform: "translateY(1.5px)" }}>{badge}</Flex>}
    </Flex>
  );
  if (helpText) {
    return <InfoTooltip label={helpText}>{title}</InfoTooltip>;
  }
  return (
    <Flex align="center" direction="row" gap="sm" w="100%">
      {title}
      <Flex style={{ flex: 1 }}>{props.children}</Flex>
    </Flex>
  );
};

const ModuleControls = ({ children, ...rest }: PropsWithChildren<FlexProps>) => (
  <Flex justify="space-between" gap="xs" wrap="wrap" align="flex-start" {...rest}>
    {children}
  </Flex>
);

const ModuleSearch = ({ placeholder, query, setQuery }: ModuleSearchProps) => {
  const { name } = useContext(ModuleHeaderContext);
  return (
    <TextInput
      placeholder={placeholder || `Search ${pluralize(name.toLowerCase())}...`}
      icon={<IconSearch size={16} />}
      miw={{ base: 200, xs: 300 }}
      maw={{ base: 200, xs: 300 }}
      style={{ flex: "0 1 auto" }}
      className={styles.searchInput}
      value={query}
      onChange={event => setQuery(event.currentTarget.value)}
    />
  );
};

const ModuleExport = forwardRef<HTMLButtonElement, ModuleExportProps>(
  ({ exportOnClick, exportIsLoading, rightIcon, ...others }: ModuleExportProps, ref) => (
    <Button
      ref={ref}
      disabled={exportIsLoading}
      leftIcon={exportIsLoading ? <Loader size={16} /> : <IconFileExport size={16} />}
      rightIcon={rightIcon}
      variant="outline"
      onClick={exportOnClick}
      {...others}
    >
      Export
    </Button>
  ),
);
ModuleExport.displayName = "ModuleExport";

const ModuleFilters = ({ nFiltersApplied, openFilterDrawer }: ModuleFiltersProps) => (
  <FiltersButton nFiltersApplied={nFiltersApplied || 0} onClick={openFilterDrawer} />
);

const ModuleNew = ({ label, openCreateModal, disabled, badgeCount }: ModuleNewProps) => {
  const { name } = useContext(ModuleHeaderContext);
  return (
    <Button
      disabled={disabled}
      leftIcon={<IconPlus size={16} />}
      rightIcon={
        badgeCount !== undefined && badgeCount > 0 ? (
          <Badge size="sm" color="red" variant="filled" style={{ pointerEvents: "none" }}>
            {badgeCount}
          </Badge>
        ) : undefined
      }
      onClick={openCreateModal}
    >
      {label || `New ${name}`}
    </Button>
  );
};

const ModuleDescription = () => {
  const { description, descriptionLinkHref, descriptionLinkLabel } = useContext(ModuleHeaderContext);
  if (!description) {
    return <></>;
  }
  return (
    <Flex>
      <Text size="sm" c="dimmed" style={{ lineHeight: 1.5 }}>
        {description}
        {descriptionLinkHref && (
          <>
            {" "}
            <Anchor
              href={descriptionLinkHref}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              c="primary"
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              {descriptionLinkLabel || "Learn more"}
              <IconExternalLink size={12} style={{ transform: "translateY(1px)" }} />
            </Anchor>
          </>
        )}
      </Text>
    </Flex>
  );
};

export const ModuleHeader = (props: PropsWithChildren<ModuleHeaderProps>) => {
  return (
    <ModuleHeaderContext.Provider
      value={{
        name: props.name,
        description: props.description,
        descriptionLinkHref: props.descriptionLinkHref,
        descriptionLinkLabel: props.descriptionLinkLabel,
      }}
    >
      <Flex direction="column" gap="sm" mb="md" className={styles.toolbarStack}>
        {props.children}
      </Flex>
    </ModuleHeaderContext.Provider>
  );
};

ModuleHeader.Controls = ModuleControls;
ModuleHeader.Description = ModuleDescription;
ModuleHeader.Export = ModuleExport;
ModuleHeader.Filters = ModuleFilters;
ModuleHeader.New = ModuleNew;
ModuleHeader.Search = ModuleSearch;
ModuleHeader.Title = ModuleTitle;
